import { kybService } from "@services/apis/Kyb";
import { KybLinkRecord } from "@services/models/Kyb/GetKybLinks";
import { useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZE = 1000;

export const kybErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback;

const useKybLinks = () => {
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const requestRef = useRef(0);
  const mountedRef = useRef(false);
  const recordPagesRef = useRef(new Map<string, number>());
  const pendingListRef = useRef<Promise<void> | null>(null);
  const recordRequestRef = useRef(0);
  const pendingRecordsRef = useRef(new Map<string, number>());
  const [records, setRecords] = useState<KybLinkRecord[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchKybLinks = useCallback((page: number, append: boolean) => {
    const request = ++requestRef.current;
    loadingRef.current = true;
    setIsLoading(true);
    setError("");

    const pending = (async () => {
      try {
        const pages = append
          ? [page]
          : Array.from({ length: page }, (_, index) => index + 1);
        const responses = await Promise.all(
          pages.map((pageNumber) =>
            kybService.getKybLinks(pageNumber, PAGE_SIZE),
          ),
        );
        if (request !== requestRef.current) return;
        const fetched = responses.flatMap(
          (response) => response.data?.records ?? [],
        );
        const last = responses[responses.length - 1]?.data;
        const recordPages = append
          ? new Map(recordPagesRef.current)
          : new Map<string, number>();
        responses.forEach((response, index) => {
          response.data?.records?.forEach((record) => {
            const id = String(record.id);
            if (!recordPages.has(id)) recordPages.set(id, pages[index]);
          });
        });
        recordPagesRef.current = recordPages;

        setRecords((previous) => {
          const seen = new Set<string>();
          return (append ? [...previous, ...fetched] : fetched).filter(
            (record) => {
              const id = String(record.id);
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            },
          );
        });
        setHasMore(
          last?.pagination?.hasNextPage ?? last?.records?.length === PAGE_SIZE,
        );
        setTotalRecords(last?.pagination?.totalRecords ?? null);
        pageRef.current = page;
      } catch (err) {
        if (request === requestRef.current)
          setError(
            kybErrorMessage(err, "Could not load KYB links. Please try again."),
          );
      } finally {
        if (request === requestRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    })();

    pendingListRef.current = pending;
    void pending.finally(() => {
      if (pendingListRef.current === pending) pendingListRef.current = null;
    });
    return pending;
  }, []);

  useEffect(() => {
    const pendingRecords = pendingRecordsRef.current;
    mountedRef.current = true;
    void fetchKybLinks(1, false);
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      pendingRecords.clear();
    };
  }, [fetchKybLinks]);

  const refreshRecords = useCallback(() => {
    void fetchKybLinks(pageRef.current, false);
  }, [fetchKybLinks]);

  const updateRecord = useCallback((updated: KybLinkRecord) => {
    setRecords((previous) =>
      previous.map((record) =>
        String(record.id) === String(updated.id) ? updated : record,
      ),
    );
  }, []);

  const refreshRecord = useCallback(
    async (id: KybLinkRecord["id"]) => {
      const key = String(id);
      const request = ++recordRequestRef.current;
      pendingRecordsRef.current.set(key, request);
      setError("");
      const isCurrent = () =>
        mountedRef.current && pendingRecordsRef.current.get(key) === request;
      try {
        while (isCurrent()) {
          // Wait for list replacements so an older list response cannot undo this update.
          if (pendingListRef.current) {
            await pendingListRef.current;
            continue;
          }
          const page = recordPagesRef.current.get(key);
          if (page === undefined) return;
          const listRequest = requestRef.current;
          const response = await kybService.getKybLinks(page, PAGE_SIZE);
          if (!isCurrent()) return;
          if (listRequest !== requestRef.current) continue;
          const updated = response.data?.records?.find(
            (record) => String(record.id) === key,
          );
          if (updated) {
            updateRecord(updated);
          } else {
            // Server ordering changed; resync page boundaries rather than guessing.
            await fetchKybLinks(pageRef.current, false);
          }
          return;
        }
      } catch (err) {
        if (isCurrent())
          setError(
            kybErrorMessage(
              err,
              "Could not refresh the KYB record. Please try again.",
            ),
          );
      } finally {
        if (pendingRecordsRef.current.get(key) === request)
          pendingRecordsRef.current.delete(key);
      }
    },
    [fetchKybLinks, updateRecord],
  );

  const resetRecords = useCallback(() => {
    void fetchKybLinks(1, false);
  }, [fetchKybLinks]);

  const loadMoreRecords = useCallback(() => {
    if (loadingRef.current) return;
    void fetchKybLinks(pageRef.current + 1, true);
  }, [fetchKybLinks]);

  const removeRecord = useCallback((id: KybLinkRecord["id"]) => {
    const key = String(id);
    const wasLoaded = recordPagesRef.current.delete(key);
    pendingRecordsRef.current.delete(key);
    setRecords((previous) =>
      previous.filter((record) => String(record.id) !== key),
    );
    if (wasLoaded)
      setTotalRecords((total) =>
        total == null ? null : Math.max(0, total - 1),
      );
  }, []);

  return {
    records,
    hasMore,
    totalRecords,
    isLoading,
    error,
    refreshRecords,
    refreshRecord,
    resetRecords,
    loadMoreRecords,
    updateRecord,
    removeRecord,
  };
};

export default useKybLinks;
